import {
  LineStyle,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type SeriesType,
} from 'lightweight-charts';
import type { ChartColors } from './chartColors';
import { snapPrice, type ChartLine, type SymbolPrecision } from './chartTrading';

/** How close, in pixels, the pointer must be to a line to grab it. */
const GRAB = 6;
/** How long a dropped line stays where it was dropped while the server's confirmation is on its way. */
const SETTLE_MS = 2000;

export interface ContextInfo {
  /** The price under the pointer, or null outside the plot area. */
  price: number | null;
  line: ChartLine | null;
  x: number;
  y: number;
}

export interface LineLayerHandlers {
  /** Called while a line is dragged (and with null when the drag ends). */
  onDragMove: (line: ChartLine | null, price: number | null) => void;
  /** Called on release; true means the server accepted it, so the line stays where it was dropped. */
  onDragEnd: (line: ChartLine, price: number) => Promise<boolean>;
  onContextMenu: (info: ContextInfo) => void;
}

/**
 * Draws the account's lines on a chart and lets the user drag them. Lightweight Charts has no draggable lines, so this
 * hit-tests pointer events itself (in the capture phase, so a grabbed line never pans the chart), converts the pointer height
 * to a price with the series' own scale, and moves the line live. Nothing is sent until the pointer is released.
 */
export class LineLayer {
  private lines: ChartLine[] = [];
  private handles = new Map<string, IPriceLine>();
  private overrides = new Map<string, { price: number; until: number }>();
  private drag: { line: ChartLine; price: number } | null = null;
  private armed = false;
  private editable = false;
  private colors!: ChartColors;
  private precision: SymbolPrecision = { digits: 5, tickSize: 0.00001 };
  private handlers!: LineLayerHandlers;
  private attachedSeries: ISeriesApi<SeriesType> | null = null;

  constructor(
    private readonly host: HTMLElement,
    private readonly chart: IChartApi,
    private readonly getSeries: () => ISeriesApi<SeriesType> | null,
  ) {
    // Where each line is drawn (pixels from the top of the chart), for end-to-end tests that must grab a line.
    const hooks = host as HTMLElement & {
      __adioxLines?: () => Record<string, { y: number; price: number }>;
      __adioxY?: (price: number) => number | null;
    };
    hooks.__adioxLines = () => this.positions();
    hooks.__adioxY = (price) => this.getSeries()?.priceToCoordinate(price) ?? null;
    host.addEventListener('pointerdown', this.onPointerDown, true);
    host.addEventListener('mousedown', this.onMouseDown, true);
    host.addEventListener('pointermove', this.onHover);
    host.addEventListener('contextmenu', this.onContext);
  }

  destroy() {
    this.host.removeEventListener('pointerdown', this.onPointerDown, true);
    this.host.removeEventListener('mousedown', this.onMouseDown, true);
    this.host.removeEventListener('pointermove', this.onHover);
    this.host.removeEventListener('contextmenu', this.onContext);
    this.stopDragListeners();
    this.handles.clear();
  }

  update(
    lines: ChartLine[],
    editable: boolean,
    colors: ChartColors,
    precision: SymbolPrecision,
    handlers: LineLayerHandlers,
  ) {
    this.lines = lines;
    this.editable = editable;
    this.colors = colors;
    this.precision = precision;
    this.handlers = handlers;
    if (!editable && this.drag) this.cancelDrag();
    this.render();
  }

  /** Redraws the lines. Also called after the series was replaced, when every handle is stale. */
  render() {
    const series = this.getSeries();
    if (!series) return;
    if (series !== this.attachedSeries) {
      this.handles.clear();
      this.attachedSeries = series;
    }
    const now = Date.now();
    const seen = new Set<string>();
    for (const line of this.lines) {
      seen.add(line.id);
      const held = this.overrides.get(line.id);
      if (held && (held.until < now || held.price === line.price)) this.overrides.delete(line.id);
      const price =
        this.drag?.line.id === line.id ? this.drag.price : (this.overrides.get(line.id)?.price ?? line.price);
      const options = this.optionsOf(line, price);
      const handle = this.handles.get(line.id);
      if (handle) handle.applyOptions(options);
      else this.handles.set(line.id, series.createPriceLine(options));
    }
    for (const [id, handle] of this.handles) {
      if (!seen.has(id)) {
        series.removePriceLine(handle);
        this.handles.delete(id);
        this.overrides.delete(id);
      }
    }
  }

  /** The price range the lines occupy (not counting one being dragged, so the scale does not move under the pointer). */
  extent(): { min: number; max: number } | null {
    let min = Infinity;
    let max = -Infinity;
    for (const line of this.lines) {
      if (this.drag?.line.id === line.id) continue;
      const price = this.overrides.get(line.id)?.price ?? line.price;
      min = Math.min(min, price);
      max = Math.max(max, price);
    }
    return Number.isFinite(min) ? { min, max } : null;
  }

  private positions(): Record<string, { y: number; price: number }> {
    const series = this.getSeries();
    const out: Record<string, { y: number; price: number }> = {};
    if (!series) return out;
    for (const line of this.lines) {
      const price = this.overrides.get(line.id)?.price ?? line.price;
      const y = series.priceToCoordinate(price);
      if (y !== null) out[line.id] = { y, price };
    }
    return out;
  }

  private optionsOf(line: ChartLine, price: number) {
    const c = this.colors;
    const isStop = line.kind.endsWith('Sl');
    const isTarget = line.kind.endsWith('Tp');
    const color = isStop ? c.down : isTarget ? c.up : line.side === 'Buy' ? c.up : c.down;
    const isPosition = line.kind === 'position';
    return {
      price,
      color,
      lineWidth: (isPosition ? 2 : 1) as 1 | 2,
      lineStyle: isPosition ? LineStyle.Solid : isStop || isTarget ? LineStyle.Dashed : LineStyle.LargeDashed,
      axisLabelVisible: true,
      title: line.title,
    };
  }

  // ------------------------------------------------------------------ pointer handling
  private plotBox() {
    const box = this.host.getBoundingClientRect();
    const scaleWidth = this.chart.priceScale('right').width();
    return { box, right: box.width - scaleWidth };
  }

  private lineAt(x: number, y: number): ChartLine | null {
    const series = this.getSeries();
    if (!series || !this.editable) return null;
    const { right } = this.plotBox();
    if (x < 0 || x > right) return null;
    let best: { line: ChartLine; distance: number } | null = null;
    for (const line of this.lines) {
      const at = series.priceToCoordinate(this.overrides.get(line.id)?.price ?? line.price);
      if (at === null) continue;
      const distance = Math.abs(at - y);
      if (distance <= GRAB && (!best || distance < best.distance)) best = { line, distance };
    }
    return best?.line ?? null;
  }

  private priceAt(y: number): number | null {
    const price = this.getSeries()?.coordinateToPrice(y);
    return price === null || price === undefined ? null : snapPrice(price, this.precision);
  }

  private readonly onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0 || !this.editable) return;
    const { box } = this.plotBox();
    const line = this.lineAt(e.clientX - box.left, e.clientY - box.top);
    if (!line) return;
    e.stopImmediatePropagation(); // and nothing else on the chart (a drawing) may take this click
    e.preventDefault();
    this.armed = true;
    this.drag = { line, price: this.overrides.get(line.id)?.price ?? line.price };
    window.addEventListener('pointermove', this.onDragMove);
    window.addEventListener('pointerup', this.onDragUp);
    window.addEventListener('pointercancel', this.onDragCancel);
    window.addEventListener('keydown', this.onKey, true);
    this.host.classList.add('line-dragging');
    this.handlers.onDragMove(line, this.drag.price);
  };

  /** The chart listens for mouse events too; a grabbed line must not start a pan there either. */
  private readonly onMouseDown = (e: MouseEvent) => {
    if (this.armed) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  private readonly onHover = (e: PointerEvent) => {
    if (this.drag) return;
    const { box } = this.plotBox();
    this.host.classList.toggle('line-hover', this.lineAt(e.clientX - box.left, e.clientY - box.top) !== null);
  };

  private readonly onDragMove = (e: PointerEvent) => {
    if (!this.drag) return;
    const { box } = this.plotBox();
    const price = this.priceAt(e.clientY - box.top);
    if (price === null || !(price > 0)) return;
    this.drag = { ...this.drag, price };
    this.render();
    this.handlers.onDragMove(this.drag.line, price);
  };

  private readonly onDragUp = () => {
    const drag = this.drag;
    this.finishDrag();
    if (!drag) return;
    void this.handlers.onDragEnd(drag.line, drag.price).then((accepted) => {
      // The open-price line only hands out a stop or target, so it goes straight back; the new line arrives with the update.
      if (accepted && drag.price !== drag.line.price && drag.line.kind !== 'position') {
        this.overrides.set(drag.line.id, { price: drag.price, until: Date.now() + SETTLE_MS });
      }
      this.render();
    });
  };

  private readonly onDragCancel = () => this.cancelDrag();

  private readonly onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this.cancelDrag();
    }
  };

  private cancelDrag() {
    this.finishDrag();
    this.render();
  }

  private finishDrag() {
    this.drag = null;
    this.armed = false;
    this.stopDragListeners();
    this.host.classList.remove('line-dragging');
    this.handlers.onDragMove(null, null);
  }

  private stopDragListeners() {
    window.removeEventListener('pointermove', this.onDragMove);
    window.removeEventListener('pointerup', this.onDragUp);
    window.removeEventListener('pointercancel', this.onDragCancel);
    window.removeEventListener('keydown', this.onKey, true);
  }

  private readonly onContext = (e: MouseEvent) => {
    e.preventDefault();
    const { box, right } = this.plotBox();
    const x = e.clientX - box.left;
    const y = e.clientY - box.top;
    const inPlot = x >= 0 && x <= right;
    this.handlers.onContextMenu({
      price: inPlot ? this.priceAt(y) : null,
      line: inPlot ? this.lineAt(x, y) : null,
      x: e.clientX,
      y: e.clientY,
    });
  };
}
